import { create } from "zustand";
import { persist } from "zustand/middleware";
import type {
  Conversation,
  ChannelType,
  ChannelDefinition,
  Message,
  User,
} from "@interfaces/chat.interface";

import { CHANNEL_DEFINITIONS } from "@constants/channels";
import { CHANNELS } from "@common";
import type { ConversationTabType } from "@constants/conversationTabs";
import { STORAGE_KEYS } from "@config/app.config";
import { pinConversation, unpinConversation } from "@api/services/facebook";

const channelDefinitions = CHANNEL_DEFINITIONS;

const normalizeSelectedChannel = (
  channel: ChannelType | string | undefined | null
): ChannelType => {
  if (channel === CHANNELS.FACEBOOK) return CHANNELS.FACEBOOK;
  if (channel === CHANNELS.ZALO) return CHANNELS.ZALO;
  if (channel === CHANNELS.SHOPEE) return CHANNELS.SHOPEE;
  return CHANNELS.FACEBOOK;
};

interface ConversationPagination {
  offset: number;
  total: number;
  limit: number;
  hasMore: boolean;
}

interface ChatState {
  channels: ChannelDefinition[];
  conversations: Conversation[];
  filteredConversations: Conversation[];
  selectedChannel: ChannelType;
  selectedFanpageName?: string;
  selectedPageId?: string;
  selectedConversationId?: string;
  activeTab: ConversationTabType;
  searchTerm: string;
  users: User[];
  conversationPagination: Record<string, ConversationPagination>; // key: pageId
  isSyncing: boolean;
  syncProgress: number;
  syncItemsSuccess: number;
  syncTotalItems: number;
  conversationFolderStatusMap: Record<string, string>;
  conversationTagsMap: Record<string, Array<{ description: string; id: number; tagName: string }>>;
  conversationUnreadCountMap: Record<string, number>; // Map conversationId -> unreadCount
  conversationPageMap: Record<string, string>; // Map conversationId -> pageId
  activeSettingMenuItem: string | null;
  isSwitchingFanpage: boolean;
  refreshConversationHistoryTrigger: number;
  triggerRefreshConversationHistory: () => void;
  setActiveTab: (tab: ConversationTabType) => void;
  movedConversationInfo: { conversationId: string; targetTabLabel: string } | null;
  setMovedConversationInfo: (info: { conversationId: string; targetTabLabel: string } | null) => void;
  setConversations: (conversations: Conversation[]) => void;
  setConversationsWithSearch: (conversations: Conversation[], searchTerm: string) => void;
  appendConversations: (
    conversations: Conversation[],
    pageId: string,
    pagination: ConversationPagination
  ) => void;
  selectChannel: (channel: ChannelType) => void;
  selectFanpage: (fanpageName: string, pageId: string, channelType: ChannelType) => void;
  selectConversation: (conversationId: string | undefined) => void;
  selectSettingMenuItem: (menuItemId: string | null) => void;
  searchConversation: (term: string) => void;
  togglePin: (conversationId: string, pageId?: string) => void;
  toggleMute: (conversationId: string) => void;
  markConversationAsRead: (conversationId: string) => void;
  upsertConversation: (conversation: Conversation) => void;
  addMessage: (
    conversationId: string,
    message: Message,
    isActiveConversation?: boolean
  ) => void;
  updatePresence: (userId: string, presence: User["presence"]) => void;
  setSyncProgress: (progress: number) => void;
  setIsSyncing: (isSyncing: boolean) => void;
  setSyncItems: (itemsSuccess: number, totalItems: number) => void;
  updateConversationFolderStatus: (folderStatusMap: Record<string, string>) => void;
  clearConversationFolderStatus: () => void;
  updateConversationTagsMap: (tagsMap: Record<string, Array<{ description: string; id: number; tagName: string }>>) => void;
  setIsSwitchingFanpage: (isSwitching: boolean) => void;
  clearConversations: () => void;
}

const filterConversations = (
  conversations: Conversation[],
  channel: ChannelType,
  term: string,
  fanpageName?: string
) => {
  const normalizedChannel = normalizeSelectedChannel(channel);
  let filtered = conversations.filter((conv) => conv.channel === normalizedChannel);

  if (fanpageName) {
    filtered = filtered.filter((conv) => conv.fanpageName === fanpageName);
  }

  if (term) {
    filtered = filtered.filter((conv) =>
      conv.name.toLowerCase().includes(term.toLowerCase())
    );
  }

  // Sort: pinned first, then by lastMessage timestamp (newest first)
  return filtered.sort((a, b) => {
    // Pinned conversations first
    if (b.isPinned !== a.isPinned) {
      return Number(b.isPinned) - Number(a.isPinned);
    }
    // Then sort by lastMessage timestamp (newest first)
    const aTime = a.lastMessage?.createdAt
      ? new Date(a.lastMessage.createdAt).getTime()
      : 0;
    const bTime = b.lastMessage?.createdAt
      ? new Date(b.lastMessage.createdAt).getTime()
      : 0;
    return bTime - aTime;
  });
};

export const useChatStore = create<ChatState>()(
  persist(
    (set) => ({
  channels: channelDefinitions,
  conversations: [],
  filteredConversations: [],
    selectedChannel: CHANNELS.FACEBOOK,
    selectedFanpageName: undefined,
    selectedPageId: undefined,
    selectedConversationId: undefined,
    activeTab: "ALL",
    activeSettingMenuItem: null,
  searchTerm: "",
      users: [],
      conversationPagination: {},
      isSyncing: false,
      syncProgress: 0,
      syncItemsSuccess: 0,
      syncTotalItems: 0,
      conversationFolderStatusMap: {},
      conversationTagsMap: {},
      conversationUnreadCountMap: {},
      conversationPageMap: {},
      isSwitchingFanpage: false,
      refreshConversationHistoryTrigger: 0,
      movedConversationInfo: null,
      triggerRefreshConversationHistory: () =>
        set((state) => ({
          refreshConversationHistoryTrigger: state.refreshConversationHistoryTrigger + 1,
        })),
      setActiveTab: (tab) => set({ activeTab: tab }),
      setMovedConversationInfo: (info) => set({ movedConversationInfo: info }),
  setConversations: (conversations) =>
    set((state) => {
      const updatedPageMap = { ...state.conversationPageMap };
      const mergedConversations = conversations.map((newConv) => {
        const existingConv = state.conversations.find((conv) => conv.id === newConv.id);
        if (existingConv) {
          // Preserve pageId mapping if conversation has fanpageName
          if (newConv.fanpageName && state.selectedPageId) {
            updatedPageMap[newConv.id] = state.selectedPageId;
          }
          return {
            ...newConv,
            unreadCount: existingConv.unreadCount,
          };
        }
        const persistedUnreadCount = state.conversationUnreadCountMap[newConv.id];
        if (persistedUnreadCount !== undefined) {
          // Preserve pageId mapping if conversation has fanpageName
          if (newConv.fanpageName && state.selectedPageId) {
            updatedPageMap[newConv.id] = state.selectedPageId;
          }
          return {
            ...newConv,
            unreadCount: persistedUnreadCount,
          };
        }
        // Preserve pageId mapping if conversation has fanpageName
        if (newConv.fanpageName && state.selectedPageId) {
          updatedPageMap[newConv.id] = state.selectedPageId;
        }
        return newConv;
      });
      
      return {
        conversations: mergedConversations,
        conversationPageMap: updatedPageMap,
        filteredConversations: filterConversations(
          mergedConversations,
          state.selectedChannel,
          state.searchTerm,
          state.selectedFanpageName
        ),
      };
    }),
  setConversationsWithSearch: (conversations, searchTerm) =>
    set((state) => {
      const updatedPageMap = { ...state.conversationPageMap };
      const mergedConversations = conversations.map((newConv) => {
        const existingConv = state.conversations.find((conv) => conv.id === newConv.id);
        if (existingConv) {
          // Preserve pageId mapping if conversation has fanpageName
          if (newConv.fanpageName && state.selectedPageId) {
            updatedPageMap[newConv.id] = state.selectedPageId;
          }
          return {
            ...newConv,
            unreadCount: existingConv.unreadCount,
          };
        }
        const persistedUnreadCount = state.conversationUnreadCountMap[newConv.id];
        if (persistedUnreadCount !== undefined) {
          // Preserve pageId mapping if conversation has fanpageName
          if (newConv.fanpageName && state.selectedPageId) {
            updatedPageMap[newConv.id] = state.selectedPageId;
          }
          return {
            ...newConv,
            unreadCount: persistedUnreadCount,
          };
        }
        // Preserve pageId mapping if conversation has fanpageName
        if (newConv.fanpageName && state.selectedPageId) {
          updatedPageMap[newConv.id] = state.selectedPageId;
        }
        return newConv;
      });
      
      return {
        conversations: mergedConversations,
        conversationPageMap: updatedPageMap,
        searchTerm,
        filteredConversations: filterConversations(
          mergedConversations,
          state.selectedChannel,
          searchTerm,
          state.selectedFanpageName
        ),
      };
    }),
  appendConversations: (newConversations, pageId, pagination) =>
    set((state) => {
      const updatedPageMap = { ...state.conversationPageMap };
      const conversationsToAdd = newConversations.map((newConv) => {
        // Save pageId mapping for this conversation
        if (newConv.fanpageName) {
          updatedPageMap[newConv.id] = pageId;
        }
        
        const existingConv = state.conversations.find((conv) => conv.id === newConv.id);
        if (existingConv) {
          return {
            ...newConv,
            unreadCount: existingConv.unreadCount,
          };
        }
        const persistedUnreadCount = state.conversationUnreadCountMap[newConv.id];
        if (persistedUnreadCount !== undefined) {
          return {
            ...newConv,
            unreadCount: persistedUnreadCount,
          };
        }
        return newConv;
      });

      const existingIds = new Set(state.conversations.map((c) => c.id));
      const newConversationsToAdd = conversationsToAdd.filter(
        (c) => !existingIds.has(c.id)
      );

      if (newConversationsToAdd.length === 0) {
        // No new conversations to add, just update pagination
        return {
          conversationPagination: {
            ...state.conversationPagination,
            [pageId]: pagination,
          },
          conversationPageMap: updatedPageMap,
          filteredConversations: filterConversations(
            state.conversations,
            state.selectedChannel,
            state.searchTerm,
            state.selectedFanpageName
          ),
        };
      }

      const updatedConversations = [
        ...state.conversations,
        ...newConversationsToAdd,
      ];
      const filtered = filterConversations(
        updatedConversations,
        state.selectedChannel,
        state.searchTerm,
        state.selectedFanpageName
      );

      return {
        conversations: updatedConversations,
        conversationPageMap: updatedPageMap,
        conversationPagination: {
          ...state.conversationPagination,
          [pageId]: pagination,
        },
        filteredConversations: filtered,
      };
    }),
  selectChannel: (channel) =>
    set((state) => {
      const normalizedChannel = normalizeSelectedChannel(channel);
      return {
      selectedChannel: normalizedChannel,
      selectedFanpageName: undefined,
      selectedPageId: undefined,
      selectedConversationId: undefined,
      filteredConversations: filterConversations(
        state.conversations,
        normalizedChannel,
        state.searchTerm
      ),
      };
    }),
  selectFanpage: (fanpageName, pageId, channelType) =>
    set((state) => {
      const normalizedChannel = normalizeSelectedChannel(channelType);
      const filtered = filterConversations(
        state.conversations,
        normalizedChannel,
        state.searchTerm,
        fanpageName
      );
      const shouldResetConversation = state.selectedFanpageName !== fanpageName || state.selectedPageId !== pageId;
      return {
        selectedChannel: normalizedChannel,
        selectedFanpageName: fanpageName,
        selectedPageId: pageId,
        selectedConversationId: shouldResetConversation ? undefined : state.selectedConversationId,
        filteredConversations: filtered,
        activeSettingMenuItem: null,
      };
    }),
  selectConversation: (conversationId) =>
    set({ selectedConversationId: conversationId, activeSettingMenuItem: null }),
  selectSettingMenuItem: (menuItemId) =>
    set({ 
      activeSettingMenuItem: menuItemId,
      selectedFanpageName: undefined,
      selectedPageId: undefined,
      selectedConversationId: undefined,
    }),
  searchConversation: (term) =>
    set((state) => ({
      searchTerm: term,
      filteredConversations: filterConversations(
        state.conversations,
        state.selectedChannel,
        term,
        state.selectedFanpageName
      ),
    })),
  togglePin: async (conversationId, pageId) => {
    const state = useChatStore.getState();
    const conversation = state.conversations.find((conv) => conv.id === conversationId);
    if (!conversation) return;

    const isCurrentlyPinned = conversation.isPinned;
    const newPinnedState = !isCurrentlyPinned;

    set((currentState) => {
      const conversations = currentState.conversations.map((conv) =>
        conv.id === conversationId
          ? { ...conv, isPinned: newPinnedState }
          : conv
      );
      return {
        conversations,
        filteredConversations: filterConversations(
          conversations,
          currentState.selectedChannel,
          currentState.searchTerm,
          currentState.selectedFanpageName
        ),
      };
    });

    if (pageId) {
      try {
        if (newPinnedState) {
          await pinConversation({ conversationId, pageId });
        } else {
          await unpinConversation({ conversationId, pageId });
        }
      } catch (error) {
        console.error("Error toggling pin:", error);
        set((currentState) => {
          const conversations = currentState.conversations.map((conv) =>
            conv.id === conversationId
              ? { ...conv, isPinned: isCurrentlyPinned }
              : conv
          );
          return {
            conversations,
            filteredConversations: filterConversations(
              conversations,
              currentState.selectedChannel,
              currentState.searchTerm,
              currentState.selectedFanpageName
            ),
          };
        });
      }
    }
  },
  toggleMute: (conversationId) =>
    set((state) => {
      const conversations = state.conversations.map((conv) =>
        conv.id === conversationId ? { ...conv, isMuted: !conv.isMuted } : conv
      );
      return {
        conversations,
        filteredConversations: filterConversations(
          conversations,
          state.selectedChannel,
          state.searchTerm,
          state.selectedFanpageName
        ),
      };
    }),
  markConversationAsRead: (conversationId) =>
    set((state) => {
      const conversations = state.conversations.map((conv) =>
        conv.id === conversationId ? { ...conv, unreadCount: 0 } : conv
      );
      const updatedUnreadCountMap = {
        ...state.conversationUnreadCountMap,
        [conversationId]: 0,
      };
      return {
        conversations,
        conversationUnreadCountMap: updatedUnreadCountMap,
        filteredConversations: filterConversations(
          conversations,
          state.selectedChannel,
          state.searchTerm,
          state.selectedFanpageName
        ),
      };
    }),
  upsertConversation: (conversation) =>
    set((state) => {
      const exists = state.conversations.some(
        (conv) => conv.id === conversation.id
      );
      const conversations = exists
        ? state.conversations.map((conv) => {
            if (conv.id === conversation.id) {
              return {
                ...conversation,
                unreadCount: conv.unreadCount,
              };
            }
            return conv;
          })
        : [conversation, ...state.conversations];
      return {
        conversations,
        filteredConversations: filterConversations(
          conversations,
          state.selectedChannel,
          state.searchTerm,
          state.selectedFanpageName
        ),
      };
    }),
  addMessage: (conversationId, message, isActiveConversation = false) =>
    set((state) => {
      const existingConversation = state.conversations.find(
        (conv) => conv.id === conversationId
      );
      
      if (!existingConversation) {
        // Xác định OUTBOUND: có direction = "OUTBOUND" hoặc status = "sent"/"sending"
        const isOutbound = 
          message.meta?.direction === "OUTBOUND" ||
          message.status === "sent" ||
          message.status === "sending";
        const shouldIncrementUnread = !isActiveConversation && !isOutbound;
        const persistedUnreadCount = state.conversationUnreadCountMap[conversationId] ?? 0;
        const newUnreadCount = shouldIncrementUnread
          ? persistedUnreadCount + 1
          : persistedUnreadCount;
        
        const newConversation: Conversation = {
          id: conversationId,
          channel: CHANNELS.FACEBOOK,
          name: (message.meta?.conversationName as string) || 
                (message.meta?.receiverName as string) || 
                message.sender?.name || 
                "Unknown",
          avatar: (message.meta?.conversationAvatarUrl as string) || 
                  (message.meta?.receiverAvatar as string) || 
                  message.sender?.avatar || 
                  "",
          isGroup: false,
          members: [],
          lastMessage: message,
          unreadCount: newUnreadCount,
          isPinned: false,
          isMuted: false,
          typingUsers: [],
        };
        
        const updatedConversations = [newConversation, ...state.conversations];
        const updatedUnreadCountMap = {
          ...state.conversationUnreadCountMap,
          [conversationId]: newUnreadCount,
        };
        
        return {
          conversations: updatedConversations,
          conversationUnreadCountMap: updatedUnreadCountMap,
          filteredConversations: filterConversations(
            updatedConversations,
            state.selectedChannel,
            state.searchTerm,
            state.selectedFanpageName
          ),
        };
      }
      
      const conversations = state.conversations.map((conv) => {
        if (conv.id !== conversationId) {
          return conv;
        }
        // OUTBOUND messages should never increment unread count
        // Xác định OUTBOUND: có direction = "OUTBOUND" hoặc status = "sent"/"sending"
        const isOutbound = 
          message.meta?.direction === "OUTBOUND" ||
          message.status === "sent" ||
          message.status === "sending";
        const shouldIncrementUnread = !isActiveConversation && !isOutbound;
        
        // Update avatar if conversation is active and message has avatar info
        let updatedAvatar = conv.avatar;
        if (isActiveConversation) {
          // Try multiple sources for conversation avatar
          const conversationAvatarUrl = 
            message.meta?.conversationAvatarUrl ?? 
            message.meta?.receiverAvatar;
          
          // If INBOUND message, use sender avatar as conversation avatar
          const isInbound = message.meta?.direction === "INBOUND";
          const senderAvatar = message.sender?.avatar;
          
          if (conversationAvatarUrl) {
            updatedAvatar = conversationAvatarUrl;
          } else if (isInbound && senderAvatar) {
            // For INBOUND messages, sender avatar is the conversation avatar
            updatedAvatar = senderAvatar;
          }
        }
        
        const newUnreadCount = shouldIncrementUnread
          ? conv.unreadCount + 1
          : conv.unreadCount;
        
        return {
          ...conv,
          avatar: updatedAvatar,
          lastMessage: message,
          unreadCount: newUnreadCount,
        };
      });
      
      const updatedUnreadCountMap = { ...state.conversationUnreadCountMap };
      conversations.forEach((conv) => {
        updatedUnreadCountMap[conv.id] = conv.unreadCount;
      });
      
      return {
        conversations,
        conversationUnreadCountMap: updatedUnreadCountMap,
        filteredConversations: filterConversations(
          conversations,
          state.selectedChannel,
          state.searchTerm,
          state.selectedFanpageName
        ),
      };
    }),
  updatePresence: (userId, presence) =>
    set((state) => ({
      users: state.users.map((user) =>
        user.id === userId ? { ...user, presence } : user
      ),
    })),
      setSyncProgress: (progress) => set({ syncProgress: progress }),
      setIsSyncing: (isSyncing) => set({ 
        isSyncing, 
        syncProgress: isSyncing ? 0 : 0,
        syncItemsSuccess: isSyncing ? 0 : 0,
        syncTotalItems: isSyncing ? 0 : 0,
      }),
      setSyncItems: (itemsSuccess, totalItems) => set({ 
        syncItemsSuccess: itemsSuccess, 
        syncTotalItems: totalItems 
      }),
      updateConversationFolderStatus: (folderStatusMap) =>
        set((state) => {
          const updatedMap = { ...state.conversationFolderStatusMap };
          Object.keys(folderStatusMap).forEach((key) => {
            const value = folderStatusMap[key];
            if (value && value.trim() !== "") {
              updatedMap[key] = value;
            } else {
              delete updatedMap[key];
            }
          });
          return {
            conversationFolderStatusMap: updatedMap,
          };
        }),
      clearConversationFolderStatus: () =>
        set({ conversationFolderStatusMap: {} }),
      updateConversationTagsMap: (tagsMap) =>
        set((state) => ({
          conversationTagsMap: { ...state.conversationTagsMap, ...tagsMap },
        })),
      setIsSwitchingFanpage: (isSwitching) =>
        set(() => ({
          isSwitchingFanpage: isSwitching,
        })),
      clearConversations: () =>
        set({
          conversations: [],
          filteredConversations: [],
          conversationPagination: {},
        }),
    }),
    {
      name: STORAGE_KEYS.CHAT_STATE,
      version: 1,
      merge: (persistedState, currentState) => {
        if (!persistedState || typeof persistedState !== "object") {
          return currentState;
        }

        const typedState = persistedState as Partial<ChatState>;
        return {
          ...currentState,
          ...typedState,
          selectedChannel: normalizeSelectedChannel(
            typedState.selectedChannel ?? currentState.selectedChannel
          ),
        };
      },
      migrate: (persistedState) => {
        if (!persistedState || typeof persistedState !== "object") {
          return persistedState;
        }

        const typedState = persistedState as {
          selectedChannel?: ChannelType | string | null;
        };

        return {
          ...typedState,
          selectedChannel: normalizeSelectedChannel(typedState.selectedChannel),
        };
      },
      partialize: (state) => ({
        selectedChannel: state.selectedChannel,
        selectedFanpageName: state.selectedFanpageName,
        selectedPageId: state.selectedPageId,
        selectedConversationId: state.selectedConversationId,
        conversationUnreadCountMap: state.conversationUnreadCountMap,
        conversationPageMap: state.conversationPageMap,
      }),
      storage: {
        getItem: (name) => {
          const str = localStorage.getItem(name);
          return str ? JSON.parse(str) : null;
        },
        setItem: (name, value) => {
          localStorage.setItem(name, JSON.stringify(value));
        },
        removeItem: (name) => {
          localStorage.removeItem(name);
        },
      },
    }
  )
);
